import React from 'react';
import { Automation } from '../../types/automatisation.types';
import {
  ClockIcon,
  PlayIcon,
  PauseIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  CalendarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { utcToZonedTime } from 'date-fns-tz';
import './AutomatisationItem.css';

interface AutomatisationItemProps {
  automation: Automation;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  onExecute: () => void;
  onViewHistory: () => void;
}

export const AutomatisationItem: React.FC<AutomatisationItemProps> = ({
  automation,
  onEdit,
  onDelete,
  onToggle,
  onExecute,
  onViewHistory
}) => {
  const getRecurrenceLabel = () => {
    const config = automation.recurrence_config;
    const time = `${config.hour || 0}h${(config.minute || 0).toString().padStart(2, '0')}`;

    switch (automation.recurrence_type) {
      case 'hourly':
        return `Toutes les ${config.interval || 1} heure(s)`;
      case 'daily':
        return `Tous les jours à ${time}`;
      case 'weekly': {
        const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        const selectedDays = config.daysOfWeek || (config.dayOfWeek !== undefined ? [config.dayOfWeek] : [1]);
        const dayNames = selectedDays.map((d: number) => days[d]).join(', ');
        return `Chaque ${dayNames} à ${time}`;
      }
      case 'monthly': {
        const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
        const selectedMonths = config.monthsOfYear || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
        if (selectedMonths.length === 12) {
          return `Le ${config.dayOfMonth || 1} de chaque mois à ${time}`;
        } else {
          const monthLabels = selectedMonths.map((m: number) => monthNames[m - 1]).join(', ');
          return `Le ${config.dayOfMonth || 1} en ${monthLabels} à ${time}`;
        }
      }
      default:
        return automation.recurrence_type;
    }
  };

  const getStatusColor = () => {
    switch (automation.status) {
      case 'active':
        return 'admin-automatisation-status-active';
      case 'paused':
        return 'admin-automatisation-status-paused';
      case 'archived':
        return 'admin-automatisation-status-archived';
      default:
        return '';
    }
  };

  const successRate = automation.total_executions 
    ? Math.round((automation.successful_executions || 0) / automation.total_executions * 100)
    : 0;

  return (
    <div className="admin-automatisation-item">
      <div className="admin-automatisation-item-header">
        <div className="admin-automatisation-item-title-section">
          <h3 className="admin-automatisation-item-title">{automation.name}</h3>
          <span className={`admin-automatisation-status ${getStatusColor()}`}>
            {automation.status === 'active' && <PlayIcon className="admin-automatisation-status-icon" />}
            {automation.status === 'paused' && <PauseIcon className="admin-automatisation-status-icon" />}
            {automation.status}
          </span>
        </div>
        <div className="admin-automatisation-item-actions">
          <button
            onClick={onToggle}
            className="admin-automatisation-action-btn"
            title={automation.status === 'active' ? 'Mettre en pause' : 'Activer'}
          >
            {automation.status === 'active' ? (
              <PauseIcon className="admin-automatisation-action-icon" />
            ) : (
              <PlayIcon className="admin-automatisation-action-icon" />
            )}
          </button>
          <button
            onClick={onExecute}
            className="admin-automatisation-action-btn"
            title="Exécuter maintenant"
          >
            <PlayIcon className="admin-automatisation-action-icon text-green-600" />
          </button>
          <button
            onClick={onEdit}
            className="admin-automatisation-action-btn"
            title="Modifier"
          >
            <PencilIcon className="admin-automatisation-action-icon" />
          </button>
          <button
            onClick={onViewHistory}
            className="admin-automatisation-action-btn"
            title="Historique"
          >
            <ChartBarIcon className="admin-automatisation-action-icon" />
          </button>
          <button
            onClick={onDelete}
            className="admin-automatisation-action-btn admin-automatisation-action-delete"
            title="Supprimer"
          >
            <TrashIcon className="admin-automatisation-action-icon" />
          </button>
        </div>
      </div>

      {automation.description && (
        <p className="admin-automatisation-item-description">{automation.description}</p>
      )}

      <div className="admin-automatisation-item-info">
        <div className="admin-automatisation-info-row">
          <ClockIcon className="admin-automatisation-info-icon" />
          <span className="admin-automatisation-info-label">Récurrence:</span>
          <span className="admin-automatisation-info-value">{getRecurrenceLabel()}</span>
        </div>

        {automation.next_execution_at && (
          <div className="admin-automatisation-info-row">
            <CalendarIcon className="admin-automatisation-info-icon" />
            <span className="admin-automatisation-info-label">Prochaine exécution:</span>
            <span className="admin-automatisation-info-value">
              {format(
                utcToZonedTime(new Date(automation.next_execution_at), 'Europe/Brussels'),
                'dd MMM yyyy à HH:mm',
                { locale: fr }
              )}
            </span>
          </div>
        )}

        {automation.last_execution_at && (
          <div className="admin-automatisation-info-row">
            <ClockIcon className="admin-automatisation-info-icon" />
            <span className="admin-automatisation-info-label">Dernière exécution:</span>
            <span className="admin-automatisation-info-value">
              {format(
                utcToZonedTime(new Date(automation.last_execution_at), 'Europe/Brussels'),
                'dd MMM yyyy à HH:mm',
                { locale: fr }
              )}
            </span>
          </div>
        )}
      </div>

      {(automation.total_executions ?? 0) > 0 && (
        <div className="admin-automatisation-item-stats">
          <div className="admin-automatisation-stat">
            <span className="admin-automatisation-stat-value">{automation.total_executions}</span>
            <span className="admin-automatisation-stat-label">Exécutions</span>
          </div>
          <div className="admin-automatisation-stat">
            <span className={`admin-automatisation-stat-value ${successRate < 50 ? 'text-red-600' : 'text-green-600'}`}>
              {successRate}%
            </span>
            <span className="admin-automatisation-stat-label">Succès</span>
          </div>
          {(automation.failed_executions ?? 0) > 0 && (
            <div className="admin-automatisation-stat">
              <span className="admin-automatisation-stat-value text-red-600">
                {automation.failed_executions}
              </span>
              <span className="admin-automatisation-stat-label">Échecs</span>
            </div>
          )}
        </div>
      )}

      {automation.end_date && (
        <div className="admin-automatisation-item-warning">
          <ExclamationTriangleIcon className="admin-automatisation-warning-icon" />
          <span>Fin prévue le {format(new Date(automation.end_date), 'dd MMM yyyy', { locale: fr })}</span>
        </div>
      )}
    </div>
  );
};